import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Star, CheckCircle, XCircle, Loader } from 'lucide-react';
import { api } from '../services/api';

const StarRating = ({ rating, onRate, interactive = false }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        type="button"
        disabled={!interactive}
        onClick={() => interactive && onRate(star)}
        className={`transition-all ${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'}`}
      >
        <Star 
          className={`w-10 h-10 ${
            star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
          }`} 
        />
      </button>
    ))}
  </div>
);

export default function ReviewSubmitPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedRating = Number(searchParams.get('rating'));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validToken, setValidToken] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [rating, setRating] = useState(
    Number.isInteger(requestedRating) && requestedRating >= 1 && requestedRating <= 5
      ? requestedRating
      : 0
  );
  const [comment, setComment] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      try {
        const data = await api.validateReviewToken(token);
        setValidToken(data);
      } catch (err) {
        if (err.response?.status === 400) {
          setError('This review link has already been used. Thank you for your feedback!');
        } else if (err.response?.status === 404) {
          setError('This review link is invalid or has expired.');
        } else {
          setError('Failed to load review form. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Please select a star rating');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await api.submitReviewWithToken({
        token,
        rating,
        comment: comment.trim()
      });
      setSuccess(true);
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already been used')) {
        setError('This review link has already been used. Thank you for your feedback!');
      } else {
        setError(err.response?.data?.detail || 'Failed to submit review. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader className="w-10 h-10 text-amber-600 animate-spin" />
          <p className="text-gray-500 text-sm">Loading review form...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            Your review has been submitted successfully. We truly appreciate your feedback!
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-amber-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-amber-700 transition-colors"
          >
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  if (error && !validToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Review</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-900 text-white py-3 px-6 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
          >
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <button 
            onClick={() => navigate('/')} 
            className="text-gray-600 hover:text-gray-900 text-sm font-semibold mb-2"
          >
            ← Back to Menu
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Leave a Review</h1>
          <p className="text-gray-600 mt-1">Share your experience with us</p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
          {/* Order Info */}
          {validToken && (
            <div className="mb-8 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Your Order Details</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p><strong>Customer:</strong> {validToken.customerName}</p>
                <p><strong>Order Date:</strong> {new Date(validToken.orderDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}</p>
                <p><strong>Order Total:</strong> £{validToken.orderTotal.toFixed(2)}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Star Rating */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                How would you rate your experience?
              </label>
              <StarRating rating={rating} onRate={setRating} interactive />
              {rating > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {rating === 1 && '😞 Poor'}
                  {rating === 2 && '😕 Fair'}
                  {rating === 3 && '😊 Good'}
                  {rating === 4 && '😃 Very Good'}
                  {rating === 5 && '🤩 Excellent'}
                </p>
              )}
            </div>

            {/* Comment */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Tell us more (optional)
              </label>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={4}
                maxLength={500}
                placeholder="Share your thoughts about the food, service, or delivery..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500 resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{comment.length}/500 characters</p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || rating === 0}
              className="w-full bg-amber-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Review'
              )}
            </button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-4">
            This review link can only be used once
          </p>
        </div>
      </div>
    </div>
  );
}
